/**
 * Extrai a serie de UM ativo da resposta da consolidacao de carteira.
 *
 * A rota `portfolio/consolidation` nao filtra por ativo — ela devolve o
 * breakdown de todos, agrupado por `id_dado_ativo`. Este util e quem recorta o
 * ativo pedido e entrega o formato que o `asset-performance-chart` ja consome,
 * para o componente nao conhecer o payload da consolidacao.
 *
 * Retorno no contrato do `APITemplate` (`{status, message, value}`), igual
 * `buildSuccessResponse`/`buildErrorResponse` — sucesso e "ativo sem serie"
 * sao os dois `status: 200`, distintos pelo `value` (objeto vs `null`).
 *
 * Origem de cada campo (medido em whitelabelh, cliente 12319):
 * - datas      -> `value.portfolio.data.index`
 * - rentab.    -> `value.breakdown.data[<id>].rentabilidade`
 * - PL         -> `value.breakdown.data[<id>].PL`
 * - benchmark  -> `value.portfolio.data.benchmarks[<nome>]`, com o nome vindo de
 *                 `benchmark_relativo_nome` do PROPRIO ativo (o CDI e so o fallback)
 */

import { aawzDate } from '@utils/dates/aawz-date'
import { BENCHMARK_PADRAO } from '@modules/consolidator/constants/AssetMarkers'

export const adaptAssetSeriesResponse = ({ response, idAtivo, datesBR } = {}) => {
  if (!response || response.status !== 200 || !response.value) {
    return { status: resolveStatus(response), message: undefined, value: null }
  }

  const ativo = extrairAtivo(response.value, idAtivo)
  if (!ativo) {
    return { status: 200, message: undefined, value: null }
  }

  const todasDatas = datesBR || formatarEixo(extrairDatas(response.value))
  const rentabilidade = serieAlinhada(ativo.rentabilidade, todasDatas)
  const pl = serieAlinhada(ativo.PL, todasDatas)
  if (!rentabilidade.length && !pl.length) {
    return { status: 200, message: undefined, value: null }
  }

  const inicio = primeiroDiaDoAtivo(pl)
  const nomeBenchmark = ativo.benchmark_relativo_nome || BENCHMARK_PADRAO
  const disponiveis = extrairDisponiveis(response.value)
  return {
    status: 200,
    message: undefined,
    value: {
      dates: recortar(todasDatas, inicio),
      rentabilidade_acumulada: recortar(rentabilidade, inicio),
      pl: recortar(pl, inicio),
      inicio,
      benchmark_nome: nomeBenchmark,
      benchmarks_disponiveis: disponiveis,
      benchmark: serieDoBenchmark({
        benchmarks: benchmarksDoPayload(response.value),
        nomes: [nomeBenchmark],
        inicio,
        tamanho: todasDatas.length,
      }),
    },
  }
}

/**
 * Recorta e rebaseia as series dos benchmarks PEDIDOS na janela do ativo.
 * @param {Object} params
 * @param {Object} params.benchmarks Mapa nome -> serie do payload
 * @param {String[]} params.nomes Nomes a incluir
 * @param {Number} params.inicio Indice do primeiro dia do ativo
 * @param {Number} params.tamanho Comprimento do eixo BRUTO; serie divergente e descartada
 * @returns {Object} mapa nome -> serie pronta pro grafico
 */
export const serieDoBenchmark = ({ benchmarks, nomes, inicio, tamanho } = {}) => {
  const mapa = benchmarks || {}
  const saida = {}
  for (const nome of nomes || []) {
    const serie = mapa[nome]
    if (!Array.isArray(serie)) continue
    if (tamanho && serie.length !== tamanho) continue
    saida[nome] = rebasear(recortar(serie, inicio))
  }
  return saida
}

const extrairDisponiveis = (value) =>
  Object.entries(benchmarksDoPayload(value))
    .filter(([, serie]) => Array.isArray(serie) && serie.length > 0)
    .map(([nome]) => nome)

export const formatarEixo = (datas) => datas.map(paraDataBR)

const paraDataBR = (iso) => {
  const data = aawzDate(iso)
  return data.isValid() ? data.format('DD/MM/YYYY') : iso
}

const recortar = (serie, inicio) => (inicio ? serie.slice(inicio) : serie)

const primeiroDiaDoAtivo = (pl) => {
  const i = pl.findIndex((valor) => valor !== null && valor !== undefined)
  return Math.max(i, 0)
}

const rebasear = (serie) => {
  const base = serie.find((valor) => valor !== null && valor !== undefined)
  if (base === undefined || base === 0) return serie
  return serie.map((valor) =>
    valor === null || valor === undefined ? valor : (1 + valor) / (1 + base) - 1
  )
}

const serieAlinhada = (serie, dates) => {
  if (!Array.isArray(serie) || serie.length !== dates.length) return []
  return serie
}

const resolveStatus = (response) => {
  if (response && typeof response.status === 'number') return response.status
  return 500
}

const extrairAtivo = (value, idAtivo) => {
  const breakdown = value.breakdown
  if (!breakdown || !breakdown.data || !idAtivo) return null
  return breakdown.data[String(idAtivo)] || null
}

const benchmarksDoPayload = (value) => {
  const portfolio = value && value.portfolio
  if (!portfolio || !portfolio.data) return {}
  return portfolio.data.benchmarks || {}
}

const extrairDatas = (value) => {
  const portfolio = value.portfolio
  if (!portfolio || !portfolio.data) return []
  return portfolio.data.index || []
}

